import {POS_PROVIDERS, getPosProvider} from "../../pos/providers";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { McPageHeader, McPageFooter } from "../components/McPageLayout";
import MCResponseCard from "../components/MCResponseCard";
import "./mcImportEngine.css";
import "./mcConfig.css";
import { DEFAULT_UNIT_LIBRARY, buildDefaultState, listNormalizedCategories, loadEngineState, recomputeEngineState, saveEngineState, saveSharedLogicAcrossScopes, saveUnitIntelligenceConfig, loadFlagConfig, resolveFlagConfig, hydrateFlagConfig, saveFlagConfig, saveFlagConfigAcrossScopes, MC_ENGINE_EVENT } from "../utils/mcEngineData";
import { canAccessAdminTabs, formatStoreLabel, getActiveStoreScope, useCurrentMcProfile } from "../utils/profileAccess";
import { loadPosSettings, savePosSettings } from "../utils/mcPosIntegration";
import { mcClearOperationalCache, mcGetStoragePressureReport, MC_SYNC_EVENT } from "../utils/mcCloudSync";
import { hydrateResponseDesignConfig, loadResponseDesignConfig, saveResponseDesignConfig, MC_RESPONSE_DESIGN_EVENT } from "../utils/mcResponseDesign";
import { loadSharedOrderingRecord, peekSharedOrdering, saveSharedOrdering, subscribeSharedOrdering, normalizeSharedOrdering } from "../utils/mcSharedOrderingService";
import { loadSharedUnitIntelligenceRecord, normalizeSharedUnitIntelligence, saveSharedUnitIntelligence, subscribeSharedUnitIntelligence } from "../utils/mcSharedUnitIntelligenceService";

let fallbackUnitClientId = "";
let fallbackOrderingClientId = "";

const createClientId = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const getSafeSessionClientId = (key, fallbackType) => {
  const fallbackKey = fallbackType === "ordering" ? "ordering" : "unit";
  let fallbackValue = fallbackKey === "ordering" ? fallbackOrderingClientId : fallbackUnitClientId;

  if (typeof window !== "undefined") {
    try {
      const stored = window.sessionStorage.getItem(key);
      if (stored) return stored;
    } catch (error) {
      // Browser storage can be unavailable or over quota. The in-memory ID is sufficient
      // for deduplicating this tab's shared writes without crashing Inventory Configuration.
    }
  }

  if (!fallbackValue) {
    fallbackValue = createClientId();
    if (fallbackKey === "ordering") fallbackOrderingClientId = fallbackValue;
    else fallbackUnitClientId = fallbackValue;
  }

  if (typeof window !== "undefined") {
    try {
      window.sessionStorage.setItem(key, fallbackValue);
    } catch (error) {
      // Keep the stable in-memory fallback when browser storage is full.
    }
  }

  return fallbackValue;
};

export default function MorpheusCountConfig({ embeddedSection = "", onSiteConfigurationEdited = null }) {
  const currentProfile = useCurrentMcProfile();
  const activeStoreScope = getActiveStoreScope(currentProfile);
  const [engine, setEngine] = useState(buildDefaultState(activeStoreScope));
  const engineRef = useRef(engine);
  const unitSaveTimerRef = useRef(null);
  const unitSaveQueueRef = useRef(Promise.resolve());
  const unitRevisionRef = useRef(0);
  const unitWriteRef = useRef(null);
  const unitPendingRemoteRef = useRef(null);
  const unitApplySharedConfigRef = useRef(null);
  const unitLiveStateTimerRef = useRef(null);
  const unitResponseTimerRef = useRef(null);
  const unitClientIdRef = useRef(
    typeof window !== "undefined"
      ? getSafeSessionClientId("mc-unit-intelligence-client-id", "unit")
      : "server"
  );
  const orderingWriteRef = useRef(null);
  const orderingRevisionRef = useRef(0);
  const orderingClientIdRef = useRef(
    typeof window !== "undefined"
      ? getSafeSessionClientId("mc-ordering-client-id", "ordering")
      : "server"
  );
    const [flagConfig, setFlagConfig] = useState(resolveFlagConfig(activeStoreScope));
  const [dragCategory, setDragCategory] = useState("");
  const dragCategoryRef = useRef("");
  const [dropIndex, setDropIndex] = useState(null);
  const dropIndexRef = useRef(null);
  const [orderSearch, setOrderSearch] = useState("");
  const [orderPositionDrafts, setOrderPositionDrafts] = useState({});
  const [sharedCategoryOrder, setSharedCategoryOrder] = useState(() => peekSharedOrdering(activeStoreScope));
  const [orderingSaveState, setOrderingSaveState] = useState("loading");
  const [orderingResponse, setOrderingResponse] = useState({ type: "loading", title: "Loading ordering…", detail: "Connecting to the shared Inventory Configuration source." });
  const [orderingResponseVisible, setOrderingResponseVisible] = useState(true);
  const [orderingChangedCategory, setOrderingChangedCategory] = useState("");
  const orderingResponseTimerRef = useRef(null);
  const [posSettings, setPosSettings] = useState(() => loadPosSettings(activeStoreScope));
  const [unitDrafts, setUnitDrafts] = useState({});
  const [unitSaveState, setUnitSaveState] = useState("saved");
  const [unitResponse, setUnitResponse] = useState({ type: "success", title: "Unit Intelligence ready", detail: "Shared settings are connected." });
  const [unitResponseVisible, setUnitResponseVisible] = useState(false);
  const [unitChangedTarget, setUnitChangedTarget] = useState("");
  const [cacheReport, setCacheReport] = useState(() => mcGetStoragePressureReport());
  const [cacheCleanupResult, setCacheCleanupResult] = useState(null);
  const [showCacheConfirm, setShowCacheConfirm] = useState(false);
  const [responseDesign, setResponseDesign] = useState(() => loadResponseDesignConfig(activeStoreScope));
  const [responseDesignSaveState, setResponseDesignSaveState] = useState("saved");

  const showOrderingResponse = (type, title, detail = "", options = {}) => {
    if (orderingResponseTimerRef.current) window.clearTimeout(orderingResponseTimerRef.current);
    setOrderingResponse({ type, title, detail });
    setOrderingResponseVisible(true);
    if (options.changedCategory !== undefined) setOrderingChangedCategory(options.changedCategory || "");
    if (options.autoDismiss !== false && type !== "loading" && type !== "error") {
      orderingResponseTimerRef.current = window.setTimeout(() => {
        setOrderingResponseVisible(false);
        setOrderingChangedCategory("");
      }, options.duration || 2200);
    }
  };

  useEffect(() => () => {
    if (orderingResponseTimerRef.current) window.clearTimeout(orderingResponseTimerRef.current);
  }, []);

  const showUnitResponse = (type, title, detail = "", options = {}) => {
    if (unitResponseTimerRef.current) window.clearTimeout(unitResponseTimerRef.current);
    setUnitResponse({ type, title, detail });
    setUnitResponseVisible(true);
    if (options.target !== undefined) setUnitChangedTarget(options.target || "");
    if (options.autoDismiss !== false && type !== "loading" && type !== "error") {
      unitResponseTimerRef.current = window.setTimeout(() => {
        setUnitResponseVisible(false);
        setUnitChangedTarget("");
      }, options.duration || 2400);
    }
  };

  useEffect(() => () => {
    if (unitResponseTimerRef.current) window.clearTimeout(unitResponseTimerRef.current);
  }, []);

  useEffect(() => {
    const sync = () => {
      const stored = loadEngineState(activeStoreScope);
      if (stored) { const nextEngine = recomputeEngineState(stored); engineRef.current = nextEngine; setEngine(nextEngine); } else { const nextEngine = buildDefaultState(activeStoreScope); engineRef.current = nextEngine; setEngine(nextEngine); }
      setFlagConfig(resolveFlagConfig(activeStoreScope));
      setPosSettings(loadPosSettings(activeStoreScope));
    };
    sync();
    window.addEventListener(MC_ENGINE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(MC_ENGINE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [activeStoreScope]);

  useEffect(() => {
    let cancelled = false;
    const hydrateCoreConfig = async () => {
      const hydrated = await hydrateFlagConfig(activeStoreScope);
      if (!cancelled && hydrated) setFlagConfig(hydrated);
    };
    const handleManualRefresh = (event) => {
      if (event?.detail?.source !== "manual-soft-refresh") return;
      hydrateCoreConfig();
    };

    // Fresh-on-open + explicit Morpheus refresh. No realtime listener.
    hydrateCoreConfig();
    window.addEventListener(MC_SYNC_EVENT, handleManualRefresh);
    return () => {
      cancelled = true;
      window.removeEventListener(MC_SYNC_EVENT, handleManualRefresh);
    };
  }, [activeStoreScope]);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe = () => {};
    const storedScopeState = loadEngineState(activeStoreScope);
    const orderingSharedScope = activeStoreScope !== "ALL"
      && storedScopeState?.config?.configurationScopeOverrides?.ordering !== true
      ? "ALL"
      : activeStoreScope;

    const applySharedOrder = (incomingOrder = [], meta = {}) => {
      if (cancelled) return;
      const normalized = normalizeSharedOrdering(incomingOrder);
      const incomingRevision = Number(meta?.payload?.revision || 0);
      if (incomingRevision >= orderingRevisionRef.current) orderingRevisionRef.current = incomingRevision;
      let wasLocalAcknowledgement = false;

      // Keep the optimistic order authoritative until Firestore acknowledges
      // this exact write token. Older listener snapshots may arrive several
      // seconds later and must never roll the UI back.
      if (orderingWriteRef.current) {
        const pending = orderingWriteRef.current;
        const expected = JSON.stringify(pending.order);
        const incoming = JSON.stringify(normalized);
        const incomingToken = String(meta?.payload?.writeToken || "");
        const isAcknowledgement = meta.origin === "cloud" && incomingToken && incomingToken === pending.token;

        if (isAcknowledgement) {
          wasLocalAcknowledgement = true;
          orderingWriteRef.current = null;
          setOrderingSaveState("saved");
          showOrderingResponse("success", "Ordering saved", "Verified in the shared Inventory Configuration source.", { changedCategory: pending.category || "" });
        } else if (["cloud", "cache", "storage", "hydrate"].includes(meta.origin) && incoming !== expected) {
          return;
        }
      }
      setSharedCategoryOrder(normalized);
      setEngine((current) => ({
        ...current,
        config: {
          ...(current.config || {}),
          categoryOrder: normalized,
        },
      }));
      if (!(orderingWriteRef.current && ["local", "local-event", "broadcast"].includes(meta.origin))) {
        setOrderingSaveState("saved");
      }
      if (!orderingWriteRef.current && meta.origin === "cloud" && !wasLocalAcknowledgement) {
        showOrderingResponse("sync", "Ordering updated live", "A change from another browser has been applied.", { changedCategory: "" });
      } else if (meta.origin === "hydrate") {
        showOrderingResponse("success", "Ordering ready", "Shared ordering is loaded for this site.", { changedCategory: "", duration: 1600 });
      }
    };

    const hydrateSharedOrdering = async () => {
      setOrderingSaveState("loading");
      showOrderingResponse("loading", "Loading ordering…", "Connecting to the shared Inventory Configuration source.", { autoDismiss: false });
      const cloudRecord = await loadSharedOrderingRecord(orderingSharedScope, { preferCloud: true });
      if (cancelled) return;
      orderingRevisionRef.current = Number(cloudRecord.revision || 0);
      const cloudOrder = cloudRecord.order || [];

      if (cloudOrder.length) {
        applySharedOrder(cloudOrder, { origin: "hydrate", payload: cloudRecord.payload });
      } else {
        // Phase B migration: preserve the existing Inventory Configuration order
        // the first time a scope adopts the dedicated shared ordering record.
        const legacyState = loadEngineState(activeStoreScope) || (activeStoreScope !== "ALL" ? loadEngineState("ALL") : null);
        const legacyOrder = normalizeSharedOrdering(legacyState?.config?.categoryOrder || []);
        if (legacyOrder.length) {
          applySharedOrder(legacyOrder, { origin: "migration" });
          const seeded = await saveSharedOrdering(legacyOrder, orderingSharedScope, {
            source: "ordering-phase-b-migration",
            updatedBy: currentProfile?.email || currentProfile?.name || "",
            baseRevision: orderingRevisionRef.current,
            clientId: orderingClientIdRef.current,
          });
          if (!cancelled) setOrderingSaveState(seeded.ok ? "saved" : "error");
        } else {
          applySharedOrder([], { origin: "empty" });
        }
      }

      // COST C3: configuration is fresh-on-open, not realtime. Another browser
      // sees changes when this page is reopened/refreshed.
    };

    const handleManualRefresh = (event) => {
      if (event?.detail?.source !== "manual-soft-refresh") return;
      hydrateSharedOrdering();
    };

    hydrateSharedOrdering();
    window.addEventListener(MC_SYNC_EVENT, handleManualRefresh);
    return () => {
      cancelled = true;
      unsubscribe();
      window.removeEventListener(MC_SYNC_EVENT, handleManualRefresh);
    };
  }, [activeStoreScope, currentProfile?.email, currentProfile?.name]);

  useEffect(() => {
    let cancelled = false;
    const storedScopeState = loadEngineState(activeStoreScope);
    const unitSharedScope = activeStoreScope !== "ALL"
      && storedScopeState?.config?.configurationScopeOverrides?.unitIntelligence !== true
      ? "ALL"
      : activeStoreScope;

    const applySharedUnitConfig = (incomingConfig, options = {}) => {
      if (cancelled || !incomingConfig) return;
      const normalized = normalizeSharedUnitIntelligence(incomingConfig);
      const currentEngine = engineRef.current || buildDefaultState(activeStoreScope);
      const nextEngine = recomputeEngineState({
        ...currentEngine,
        config: {
          ...(currentEngine.config || {}),
          unitLibrary: normalized.unitLibrary,
          defaultUnitRule: normalized.defaultUnitRule,
          categoryUnitRules: normalized.categoryUnitRules,
        },
      });
      engineRef.current = nextEngine;
      setEngine(nextEngine);
      saveUnitIntelligenceConfig(nextEngine.config);
      saveEngineState(nextEngine, activeStoreScope);
      if (options.savedState) setUnitSaveState(options.savedState);
    };

    unitApplySharedConfigRef.current = applySharedUnitConfig;

    const hydrateSharedUnitIntelligence = async () => {
      setUnitSaveState("saving");
      showUnitResponse("loading", "Loading Unit Intelligence…", "Connecting to the shared Inventory Configuration source.", { autoDismiss: false });
      const cloudRecord = await loadSharedUnitIntelligenceRecord(unitSharedScope, { preferCloud: true });
      if (cancelled) return;
      unitRevisionRef.current = Number(cloudRecord.revision || 0);

      if (cloudRecord.config) {
        applySharedUnitConfig(cloudRecord.config, { savedState: "saved" });
        setUnitResponseVisible(false);
        return;
      }

      // Phase B migration: seed the dedicated shared document from the existing
      // Inventory Configuration values without changing the current UI model.
      const legacyState = loadEngineState(activeStoreScope) || (activeStoreScope !== "ALL" ? loadEngineState("ALL") : null) || engineRef.current;
      const legacyConfig = normalizeSharedUnitIntelligence({
        unitLibrary: legacyState?.config?.unitLibrary,
        defaultUnitRule: legacyState?.config?.defaultUnitRule,
        categoryUnitRules: legacyState?.config?.categoryUnitRules,
      });
      applySharedUnitConfig(legacyConfig);
      const seeded = await saveSharedUnitIntelligence(legacyConfig, unitSharedScope, {
        source: "unit-intelligence-phase-b-migration",
        updatedBy: currentProfile?.email || currentProfile?.name || "",
        writeToken: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        baseRevision: unitRevisionRef.current,
        clientId: unitClientIdRef.current,
      });
      if (cancelled) return;
      if (seeded.ok && seeded.verified) {
        unitRevisionRef.current = Number(seeded.revision || unitRevisionRef.current);
        setUnitSaveState("saved");
        setUnitResponseVisible(false);
      } else {
        setUnitSaveState("error");
        showUnitResponse("error", "Unable to load Unit Intelligence", "The shared configuration could not be verified. Refresh and try again.", { autoDismiss: false });
      }
    };

    let unsubscribe = () => {};

    const subscribeToSharedUnitIntelligence = () => {
      unsubscribe = subscribeSharedUnitIntelligence(unitSharedScope, (nextConfig, meta = {}) => {
        if (cancelled || !nextConfig) return;

        const incomingRevision = Number(meta?.payload?.revision || 0);
        if (incomingRevision >= unitRevisionRef.current) unitRevisionRef.current = incomingRevision;

        const incomingClientId = String(meta?.payload?.clientId || "");
        const incomingWriteToken = String(meta?.payload?.writeToken || "");
        const isOwnWrite = incomingClientId && incomingClientId === unitClientIdRef.current;
        const isOwnAcknowledgement = Boolean(
          unitWriteRef.current
          && incomingWriteToken
          && incomingWriteToken === unitWriteRef.current.token
        );

        // Local optimistic broadcasts and our own Firestore acknowledgement do
        // not need to re-apply the same state. The save queue owns those states.
        if (["local", "local-event", "broadcast"].includes(meta.origin) || isOwnAcknowledgement || isOwnWrite) return;

        // Phase E conflict protection: while this browser owns a pending write,
        // do not let a remote listener snapshot replace the optimistic form.
        // Keep only the newest remote revision and let the guarded transaction
        // decide whether our write wins or must converge to that shared state.
        if (unitWriteRef.current && meta.origin === "cloud") {
          const pendingRevision = Number(unitPendingRemoteRef.current?.meta?.payload?.revision || 0);
          if (incomingRevision >= pendingRevision) {
            unitPendingRemoteRef.current = { config: nextConfig, meta };
          }
          return;
        }

        applySharedUnitConfig(nextConfig, { savedState: "synced" });
        showUnitResponse("sync", "Unit Intelligence updated live", "A change from another browser has been applied.", { target: "remote" });
        if (unitLiveStateTimerRef.current) window.clearTimeout(unitLiveStateTimerRef.current);
        unitLiveStateTimerRef.current = window.setTimeout(() => setUnitSaveState("saved"), 2200);
      });
    };

    const handleManualRefresh = (event) => {
      if (event?.detail?.source !== "manual-soft-refresh") return;
      hydrateSharedUnitIntelligence();
    };

    // COST C3: hydrate once on page entry; do not keep a cloud listener alive.
    hydrateSharedUnitIntelligence();
    window.addEventListener(MC_SYNC_EVENT, handleManualRefresh);
    return () => {
      cancelled = true;
      unsubscribe();
      window.removeEventListener(MC_SYNC_EVENT, handleManualRefresh);
      unitApplySharedConfigRef.current = null;
      unitPendingRemoteRef.current = null;
      if (unitLiveStateTimerRef.current) window.clearTimeout(unitLiveStateTimerRef.current);
    };
  }, [activeStoreScope, currentProfile?.email, currentProfile?.name]);

  useEffect(() => {
    let cancelled = false;
    const syncResponseDesign = () => setResponseDesign(loadResponseDesignConfig(activeStoreScope));
    const hydrateResponseDesign = async () => {
      const hydrated = await hydrateResponseDesignConfig(activeStoreScope);
      if (!cancelled && hydrated) setResponseDesign(hydrated);
    };
    const handleManualRefresh = (event) => {
      if (event?.detail?.source !== "manual-soft-refresh") return;
      hydrateResponseDesign();
    };

    syncResponseDesign();
    hydrateResponseDesign();
    // Browser cache events are free; Firestore remains fresh-on-open / refresh.
    window.addEventListener(MC_RESPONSE_DESIGN_EVENT, syncResponseDesign);
    window.addEventListener("storage", syncResponseDesign);
    window.addEventListener(MC_SYNC_EVENT, handleManualRefresh);
    return () => {
      cancelled = true;
      window.removeEventListener(MC_RESPONSE_DESIGN_EVENT, syncResponseDesign);
      window.removeEventListener("storage", syncResponseDesign);
      window.removeEventListener(MC_SYNC_EVENT, handleManualRefresh);
    };
  }, [activeStoreScope]);

  const persistResponseDesign = (next) => {
    setResponseDesignSaveState("saving");
    const saved = saveResponseDesignConfig(next, activeStoreScope);
    setResponseDesign(saved);
    window.setTimeout(() => setResponseDesignSaveState("saved"), 400);
  };

  const updateSeverityRule = (severity, field, value) => {
    persistResponseDesign({
      ...responseDesign,
      severity: { ...responseDesign.severity, [severity]: { ...responseDesign.severity[severity], [field]: value } },
    });
  };

  const updateEscalationRule = (severity, field, value) => {
    persistResponseDesign({
      ...responseDesign,
      escalation: { ...responseDesign.escalation, [severity]: { ...responseDesign.escalation[severity], [field]: value } },
    });
  };

  useEffect(() => {
    const refreshReport = () => setCacheReport(mcGetStoragePressureReport());
    refreshReport();
    window.addEventListener("storage", refreshReport);
    window.addEventListener("mc-sync", refreshReport);
    return () => {
      window.removeEventListener("storage", refreshReport);
      window.removeEventListener("mc-sync", refreshReport);
    };
  }, []);

  const runCacheCleanup = () => {
    const result = mcClearOperationalCache();
    setCacheCleanupResult(result);
    setCacheReport(mcGetStoragePressureReport());
    setShowCacheConfirm(false);
  };

  const liveCategories = useMemo(() => {
    const seen = new Set();
    const out = [];
    const add = (value) => {
      const clean = String(value || "").toLowerCase().trim();
      if (!clean || seen.has(clean)) return;
      seen.add(clean);
      out.push(clean);
    };

    const addPreparedRows = (rows = []) => {
      (Array.isArray(rows) ? rows : []).forEach((row) => {
        add(row?.category || row?.normalizedCategory || row?.output || row?.countGroup || row?.group);
      });
    };

    const addStateCategories = (state = {}) => {
      listNormalizedCategories(state.rules || []).forEach(add);
      (state.config?.categoryOrder || []).forEach(add);
      Object.keys(state.config?.categoryOwnership || {}).forEach(add);
      Object.keys(state.config?.categoryUnitRules || {}).forEach(add);
      addPreparedRows(state.preparedRows || []);
    };

    addStateCategories(engine);

    // If the active site scope has not carried the category list yet, borrow the
    // shared ALL scope so the override editor never renders as an empty panel.
    // This keeps existing category override rules visible across laptops/scopes.
    if (out.length === 0 && activeStoreScope !== "ALL") {
      addStateCategories(loadEngineState("ALL") || {});
    }

    // Last-resort fallback for fresh/demo states before a CSV has rebuilt rules.
    [
      "moonstick", "aaa indoor", "aa indoor", "aaa greendoor", "aa greendoor",
      "b+ greendoor", "aaa greenhouse", "aa greenhouse", "b+ greenhouse",
      "premium outdoor", "outdoor", "bassani pre roll", "aaa indoor pre-rolls",
      "aa indoor pre-rolls", "aaa greendoor pre-rolls", "aaa greenhouse pre-rolls",
      "dank pre-rolls", "outdoor pre-rolls", "pre-rolls", "edibles",
      "sodaze", "vapes", "vape cartridges"
    ].forEach(add);

    return out;
  }, [activeStoreScope, engine, engine.rules, engine.config?.categoryOrder, engine.config?.categoryOwnership, engine.config?.categoryUnitRules, engine.preparedRows]);

  const markUnitSave = () => {
    setUnitSaveState("saving");
    if (unitSaveTimerRef.current) window.clearTimeout(unitSaveTimerRef.current);
    unitSaveTimerRef.current = window.setTimeout(() => setUnitSaveState("saved"), 450);
  };

  const persist = (next, options = {}) => {
    const recomputed = recomputeEngineState(next);
    engineRef.current = recomputed;
    setEngine(recomputed);
    if (options.unitSave) markUnitSave();

    if (options.unitSave && activeStoreScope !== "ALL") {
      const allState = loadEngineState("ALL") || buildDefaultState("ALL");
      const nextAllState = recomputeEngineState({
        ...allState,
        rules: Array.isArray(recomputed.rules) && recomputed.rules.length ? recomputed.rules : allState.rules,
        config: {
          ...(allState.config || {}),
          unitLibrary: recomputed.config?.unitLibrary || DEFAULT_UNIT_LIBRARY,
          defaultUnitRule: recomputed.config?.defaultUnitRule || defaultUnitRule,
          categoryUnitRules: recomputed.config?.categoryUnitRules || {},
        },
      });
      saveEngineState(nextAllState, "ALL");
      saveEngineState(recomputed, activeStoreScope);
      return;
    }

    saveEngineState(recomputed, activeStoreScope);
    if (activeStoreScope === "ALL" && !options.skipSharedScopeMirror) saveSharedLogicAcrossScopes(recomputed);
  };

  const persistFlagConfig = (next) => {
    const normalized = {
      negativeFlagThreshold: next.negativeFlagThreshold === "" ? "" : Number(next.negativeFlagThreshold ?? flagConfig.negativeFlagThreshold ?? -300),
      positiveFlagThreshold: next.positiveFlagThreshold === "" ? "" : Number(next.positiveFlagThreshold ?? flagConfig.positiveFlagThreshold ?? 300),
      hideCountedInMosPdf: Boolean(next.hideCountedInMosPdf ?? flagConfig.hideCountedInMosPdf ?? false),
      hideZeroOrderItemsInMosPdf: Boolean(next.hideZeroOrderItemsInMosPdf ?? flagConfig.hideZeroOrderItemsInMosPdf ?? false),
    };
    setFlagConfig(normalized);

    // Allow operators to clear the field while typing (for example, to type a minus value).
    // Only persist once both fields contain valid numbers.
    if (normalized.negativeFlagThreshold === "" || normalized.positiveFlagThreshold === "") return;

    if (activeStoreScope === "ALL") saveFlagConfigAcrossScopes(normalized);
    else saveFlagConfig(normalized, activeStoreScope);
  };

  const updateConfig = (key, value) => {
    persist({ ...engine, config: { ...engine.config, [key]: value } });
  };

  const normalizeOrderList = (items = []) => {
    const seen = new Set();
    const out = [];
    (Array.isArray(items) ? items : []).forEach((item) => {
      const clean = String(item || "").toLowerCase().trim();
      if (!clean || seen.has(clean)) return;
      seen.add(clean);
      out.push(clean);
    });
    return out;
  };

  const persistCategoryOrder = async (nextOrder = [], changedCategory = "") => {
    if (activeStoreScope !== "ALL" && typeof onSiteConfigurationEdited === "function") {
      await onSiteConfigurationEdited("ordering");
    }
    const cleanOrder = normalizeSharedOrdering(nextOrder);
    const writeToken = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    orderingWriteRef.current = { token: writeToken, order: cleanOrder, category: changedCategory };

    // Apply once locally. Incoming stale cloud/cache echoes are ignored until
    // this exact order has been acknowledged, preventing visible twitching.
    setSharedCategoryOrder(cleanOrder);
    setOrderingSaveState("saving");
    showOrderingResponse("loading", "Saving ordering…", changedCategory ? `${changedCategory} is being verified in Firestore.` : "Your ordering change is being verified in Firestore.", { changedCategory, autoDismiss: false });
    setEngine((current) => ({
      ...current,
      config: {
        ...(current.config || {}),
        categoryOrder: cleanOrder,
        categoryOverride: activeStoreScope !== "ALL" ? true : current.config?.categoryOverride,
        configurationScopeOverrides: { ...(current.config?.configurationScopeOverrides || {}), ordering: activeStoreScope !== "ALL" },
      },
    }));

    const result = await saveSharedOrdering(cleanOrder, activeStoreScope, {
      source: "inventory-configuration-ordering",
      updatedBy: currentProfile?.email || currentProfile?.name || "",
      writeToken,
      baseRevision: orderingRevisionRef.current,
      clientId: orderingClientIdRef.current,
    });

    // The service now performs an exact Firestore read-back. This gives the
    // initiating browser a deterministic completion path even when the live
    // listener is delayed or misses the local acknowledgement snapshot.
    if (orderingWriteRef.current?.token === writeToken) {
      if (result.conflict) {
        orderingWriteRef.current = null;
        orderingRevisionRef.current = Number(result.revision || orderingRevisionRef.current);
        const currentOrder = normalizeSharedOrdering(result.currentOrder || []);
        if (currentOrder.length) {
          setSharedCategoryOrder(currentOrder);
          setEngine((current) => ({ ...current, config: { ...(current.config || {}), categoryOrder: currentOrder } }));
        }
        setOrderingSaveState("saved");
        showOrderingResponse("warning", "Ordering changed by another user", "Their newer shared order was kept. Review the sequence and repeat your move if it is still needed.", { changedCategory: "", autoDismiss: false });
      } else if (result.ok && result.verified) {
        orderingWriteRef.current = null;
        orderingRevisionRef.current = Number(result.revision || orderingRevisionRef.current);
        setOrderingSaveState("saved");
        showOrderingResponse("success", "Ordering saved", "Verified in the shared Inventory Configuration source.", { changedCategory });
      } else {
        orderingWriteRef.current = null;
        setOrderingSaveState("error");
        showOrderingResponse("error", "Unable to verify ordering", result.cloudSaved ? "Firestore accepted the write, but verification did not complete. Try the action again." : "The local order is still visible. Try the action again.", { changedCategory, autoDismiss: false });
      }
    }
    return Boolean(result.ok && result.verified);
  };

  const unitLibrary = Array.isArray(engine.config.unitLibrary) && engine.config.unitLibrary.length ? engine.config.unitLibrary : DEFAULT_UNIT_LIBRARY;
  const defaultUnitRule = engine.config.defaultUnitRule || { countUnit: "Unit", baseUnit: "Unit", systemUnitSize: 1, priceUnitSize: 1, priceBasis: "count" };
  const categoryUnitRules = engine.config.categoryUnitRules && typeof engine.config.categoryUnitRules === "object" && !Array.isArray(engine.config.categoryUnitRules) ? engine.config.categoryUnitRules : {};
  const unitScopePrefix = activeStoreScope === "ALL"
    ? "Global"
    : engine.config?.categoryOverride === true
      ? "Site override"
      : "Inherited from All Sites";

  const getLatestUnitConfig = () => {
    const currentEngine = engineRef.current || engine;
    const currentConfig = currentEngine.config || {};
    return {
      currentEngine,
      currentUnitLibrary: Array.isArray(currentConfig.unitLibrary) && currentConfig.unitLibrary.length ? currentConfig.unitLibrary : DEFAULT_UNIT_LIBRARY,
      currentDefaultUnitRule: currentConfig.defaultUnitRule || { countUnit: "Unit", baseUnit: "Unit", systemUnitSize: 1, priceUnitSize: 1, priceBasis: "count" },
      currentCategoryUnitRules: currentConfig.categoryUnitRules && typeof currentConfig.categoryUnitRules === "object" && !Array.isArray(currentConfig.categoryUnitRules) ? currentConfig.categoryUnitRules : {},
    };
  };

  const updateUnitConfig = async (patch = {}, responseMeta = {}) => {
    if (activeStoreScope !== "ALL" && typeof onSiteConfigurationEdited === "function") {
      await onSiteConfigurationEdited("unit-intelligence");
    }
    const { currentEngine, currentUnitLibrary, currentDefaultUnitRule, currentCategoryUnitRules } = getLatestUnitConfig();
    const sharedConfig = normalizeSharedUnitIntelligence({
      unitLibrary: currentUnitLibrary,
      defaultUnitRule: currentDefaultUnitRule,
      categoryUnitRules: currentCategoryUnitRules,
      ...patch,
    });
    const nextConfig = {
      ...(currentEngine.config || {}),
      unitLibrary: sharedConfig.unitLibrary,
      defaultUnitRule: sharedConfig.defaultUnitRule,
      categoryUnitRules: sharedConfig.categoryUnitRules,
      categoryOverride: activeStoreScope !== "ALL" ? true : currentEngine.config?.categoryOverride,
      configurationScopeOverrides: { ...(currentEngine.config?.configurationScopeOverrides || {}), unitIntelligence: activeStoreScope !== "ALL" },
    };

    // Apply immediately so the existing Unit Intelligence UI remains fast.
    // The dedicated shared document is now the authoritative persistence path.
    const nextEngine = recomputeEngineState({ ...currentEngine, config: nextConfig });
    engineRef.current = nextEngine;
    setEngine(nextEngine);
    saveUnitIntelligenceConfig(nextConfig);
    saveEngineState(nextEngine, activeStoreScope);
    setUnitSaveState("saving");
    showUnitResponse("loading", responseMeta.title || "Saving Unit Intelligence…", responseMeta.detail || "Your change is being verified in Firestore.", { target: responseMeta.target || "general", autoDismiss: false });

    const writeToken = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    unitWriteRef.current = { token: writeToken, config: sharedConfig };
    unitSaveQueueRef.current = unitSaveQueueRef.current
      .catch(() => null)
      .then(async () => {
        const result = await saveSharedUnitIntelligence(sharedConfig, activeStoreScope, {
          source: "inventory-configuration-unit-intelligence",
          updatedBy: currentProfile?.email || currentProfile?.name || "",
          writeToken,
          baseRevision: unitRevisionRef.current,
          clientId: unitClientIdRef.current,
        });
        if (result.ok && result.verified) {
          unitRevisionRef.current = Number(result.revision || unitRevisionRef.current);
          if (unitWriteRef.current?.token === writeToken) unitWriteRef.current = null;

          const pendingRemote = unitPendingRemoteRef.current;
          unitPendingRemoteRef.current = null;
          const pendingRevision = Number(pendingRemote?.meta?.payload?.revision || 0);
          if (pendingRemote?.config && pendingRevision > Number(result.revision || 0)) {
            unitRevisionRef.current = pendingRevision;
            unitApplySharedConfigRef.current?.(pendingRemote.config, { savedState: "synced" });
            setUnitSaveState("synced");
            showUnitResponse("sync", "Unit Intelligence updated after your save", "A newer change from another browser is now active.", { target: "remote" });
          } else {
            setUnitSaveState("saved");
            showUnitResponse("success", "Unit Intelligence saved", "Verified in the shared Inventory Configuration source.", { target: responseMeta.target || "general" });
          }
        } else if (result.conflict && result.currentConfig) {
          if (unitWriteRef.current?.token === writeToken) unitWriteRef.current = null;
          unitPendingRemoteRef.current = null;
          unitRevisionRef.current = Number(result.revision || unitRevisionRef.current);
          const currentShared = normalizeSharedUnitIntelligence(result.currentConfig);
          const latestEngine = engineRef.current || nextEngine;
          const reconciled = recomputeEngineState({
            ...latestEngine,
            config: {
              ...(latestEngine.config || {}),
              unitLibrary: currentShared.unitLibrary,
              defaultUnitRule: currentShared.defaultUnitRule,
              categoryUnitRules: currentShared.categoryUnitRules,
            },
          });
          engineRef.current = reconciled;
          setEngine(reconciled);
          saveUnitIntelligenceConfig(reconciled.config);
          saveEngineState(reconciled, activeStoreScope);
          setUnitSaveState("conflict");
          const changedBy = String(result.currentPayload?.updatedBy || "another user").trim();
          showUnitResponse("warning", "Unit Intelligence changed by another user", `${changedBy} saved a newer shared configuration first. Their values were kept; review and repeat your change only if still required.`, { target: "remote", autoDismiss: false });
        } else {
          if (unitWriteRef.current?.token === writeToken) unitWriteRef.current = null;
          unitPendingRemoteRef.current = null;
          setUnitSaveState("error");
          showUnitResponse("error", "Unable to save Unit Intelligence", "The local value is still visible, but Firestore verification failed. Try the change again.", { target: responseMeta.target || "general", autoDismiss: false });
        }
        return result;
      });
  };

  const updateDefaultUnitRule = (patch = {}) => {
    const { currentDefaultUnitRule } = getLatestUnitConfig();
    updateUnitConfig({ defaultUnitRule: { ...currentDefaultUnitRule, ...patch } }, { target: "default", detail: "The default unit rule is being verified in Firestore." });
  };

  const updateCategoryUnitRule = (category, patch = {}) => {
    const key = String(category || "").toLowerCase().trim();
    if (!key) return;
    const { currentDefaultUnitRule, currentCategoryUnitRules } = getLatestUnitConfig();
    const nextRule = { ...currentDefaultUnitRule, ...(currentCategoryUnitRules[key] || {}), ...patch };
    updateUnitConfig({ categoryUnitRules: { ...currentCategoryUnitRules, [key]: nextRule } }, { target: `category:${key}`, detail: `${category} is being verified in Firestore.` });
  };

  const setUnitNumberDraft = (draftKey, rawValue, commit) => {
    const raw = String(rawValue ?? "").replace(",", ".");
    setUnitDrafts((current) => ({ ...current, [draftKey]: raw }));
    if (raw === "") return;
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && parsed > 0) commit(parsed);
  };

  const clearUnitNumberDraft = (draftKey, currentValue, commit) => {
    const raw = String(unitDrafts[draftKey] ?? "").trim();
    if (raw === "") commit(1);
    setUnitDrafts((current) => {
      const next = { ...current };
      delete next[draftKey];
      return next;
    });
  };

  const clearCategoryUnitRule = (category) => {
    const key = String(category || "").toLowerCase().trim();
    if (!key) return;
    const { currentCategoryUnitRules } = getLatestUnitConfig();
    const nextRules = { ...currentCategoryUnitRules };
    delete nextRules[key];
    updateUnitConfig({ categoryUnitRules: nextRules }, { target: `category:${key}`, detail: `${category} is being reset and verified in Firestore.` });
  };

  const clearAllCategoryUnitRules = () => {
    const { currentCategoryUnitRules } = getLatestUnitConfig();
    if (!Object.keys(currentCategoryUnitRules || {}).length) return;
    updateUnitConfig({ categoryUnitRules: {} }, {
      target: "all-categories",
      title: "Resetting category overrides…",
      detail: "Every category will use the current default Unit Rule unless changed individually again.",
    });
  };

  const addUnitToLibrary = (value) => {
    const clean = String(value || "").trim();
    if (!clean) return;
    const { currentUnitLibrary } = getLatestUnitConfig();
    const next = Array.from(new Set([...currentUnitLibrary, clean]));
    updateUnitConfig({ unitLibrary: next }, { target: "library", detail: `${clean} is being added to the shared unit library.` });
  };

  const updatePosSettings = (patch) => {
    const next = savePosSettings(patch, activeStoreScope);
    setPosSettings(next);
    persist({
      ...engine,
      config: {
        ...engine.config,
        dataSourceMode: next.dataSourceMode || "csv",
        posProvider: next.provider || "lightspeed",
      },
    });
  };

  const updateOutletMapping = (value) => {
    const scopeKey = activeStoreScope || "ALL";
    updatePosSettings({ outletMappings: { ...(posSettings.outletMappings || {}), [scopeKey]: value } });
  };

  const updatePosSyncFilters = (patch = {}) => {
    updatePosSettings({
      syncFilters: {
        ...(posSettings.syncFilters || {}),
        ...patch,
      },
    });
  };

  const updatePosSyncFilter = (key, value) => {
    updatePosSyncFilters({ [key]: value });
  };

  const syncFilters = posSettings.syncFilters || {};
  const tagModeOptions = new Set(["all", "include_tag", "exclude_tag"]);
  const tagFilterMode = tagModeOptions.has(syncFilters.inclusionMode) ? syncFilters.inclusionMode : "all";
  const categoryFilterMode = syncFilters.categoryMode || (syncFilters.inclusionMode === "exclude_categories" ? "exclude_categories" : "include_categories");
  const tagFilterValue = tagFilterMode === "exclude_tag" ? (syncFilters.excludeTag || syncFilters.tagName || "") : (syncFilters.includeTag || syncFilters.tagName || "");
  const categoryFilterValue = categoryFilterMode === "exclude_categories" ? (syncFilters.excludedCategories || syncFilters.categoryName || "") : (syncFilters.includedCategories || syncFilters.categoryName || "");
  const categoryFiltersDisabled = tagFilterMode === "all";

  const updateTagFilterMode = (mode) => {
    const currentValue = tagFilterValue;
    updatePosSyncFilters({
      inclusionMode: mode,
      tagMode: mode,
      // Keep the typed tag value even when Count All disables the input.
      // This lets admins switch back to include/exclude without retyping.
      tagName: currentValue,
      includeTag: mode === "include_tag" ? currentValue : (syncFilters.includeTag || ""),
      excludeTag: mode === "exclude_tag" ? currentValue : (syncFilters.excludeTag || ""),
    });
  };

  const updateTagFilterValue = (value) => {
    updatePosSyncFilters({
      tagName: value,
      includeTag: tagFilterMode === "include_tag" ? value : (syncFilters.includeTag || ""),
      excludeTag: tagFilterMode === "exclude_tag" ? value : (syncFilters.excludeTag || ""),
    });
  };

  const updateCategoryFilterMode = (mode) => {
    const currentValue = categoryFilterValue;
    updatePosSyncFilters({
      categoryMode: mode,
      categoryName: currentValue,
      // Keep both category memories so disabled/inactive fields do not wipe rules.
      includedCategories: mode === "include_categories" ? currentValue : (syncFilters.includedCategories || ""),
      excludedCategories: mode === "exclude_categories" ? currentValue : (syncFilters.excludedCategories || ""),
    });
  };

  const updateCategoryFilterValue = (value) => {
    updatePosSyncFilters({
      categoryName: value,
      includedCategories: categoryFilterMode === "include_categories" ? value : (syncFilters.includedCategories || ""),
      excludedCategories: categoryFilterMode === "exclude_categories" ? value : (syncFilters.excludedCategories || ""),
    });
  };

  const toggleConversionCategory = (category) => {
    const current = new Set((engine.config.conversionCategories || engine.config.halfGramCategories || []).map((item) => String(item).toLowerCase()));
    const norm = String(category).toLowerCase();
    if (current.has(norm)) current.delete(norm); else current.add(norm);
    updateConfig("conversionCategories", Array.from(current));
  };

  const configuredOutputs = useMemo(() => {
    const seen = new Set();
    const outputs = [];
    const add = (value) => {
      const clean = String(value || "").toLowerCase().trim();
      if (!clean || seen.has(clean)) return;
      seen.add(clean);
      outputs.push(clean);
    };

    const addFromState = (state = {}) => {
      listNormalizedCategories(state.rules || []).forEach(add);
      Object.keys(state.config?.categoryOwnership || {}).forEach(add);
      Object.keys(state.config?.categoryUnitRules || {}).forEach(add);
      (state.config?.categoryOrder || []).forEach(add);
    };

    addFromState(engine);
    if (!outputs.length && activeStoreScope !== "ALL") {
      addFromState(loadEngineState("ALL") || {});
    }
    return outputs;
  }, [activeStoreScope, engine.rules, engine.config?.categoryOwnership, engine.config?.categoryUnitRules, engine.config?.categoryOrder]);

  const orderedCategories = useMemo(() => {
    const storedOrder = normalizeOrderList(sharedCategoryOrder.length ? sharedCategoryOrder : (engine.config.categoryOrder || []));
    const seen = new Set(storedOrder);
    configuredOutputs.forEach((output) => {
      if (!seen.has(output)) {
        seen.add(output);
        storedOrder.push(output);
      }
    });
    return storedOrder;
  }, [configuredOutputs, engine.config?.categoryOrder, sharedCategoryOrder]);

  const setActiveDropIndex = (nextIndex) => {
    if (dropIndexRef.current === nextIndex) return;
    dropIndexRef.current = nextIndex;
    setDropIndex(nextIndex);
  };

  const clearDragState = () => {
    dragCategoryRef.current = "";
    dropIndexRef.current = null;
    setDragCategory("");
    setDropIndex(null);
  };

  const resolveDraggedCategory = (event) => {
    const dropped = event?.dataTransfer?.getData("text/plain") || "";
    return dropped || dragCategoryRef.current || dragCategory;
  };

  const getCurrentOutputOrder = () => [...orderedCategories];

  const moveCategoryToIndex = (dragged, rawTargetIndex) => {
    const order = getCurrentOutputOrder();
    const cleanDragged = String(dragged || "").toLowerCase().trim();
    const from = order.indexOf(cleanDragged);
    if (from < 0) return;

    const boundedTarget = Math.max(0, Math.min(Number(rawTargetIndex) || 0, order.length));
    const [item] = order.splice(from, 1);
    const insertAt = Math.max(0, Math.min(from < boundedTarget ? boundedTarget - 1 : boundedTarget, order.length));
    if (insertAt === from) return;
    order.splice(insertAt, 0, item);
    persistCategoryOrder(order, cleanDragged);
  };

  const handleRowDragOver = (event, rowIndex) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    const rect = event.currentTarget.getBoundingClientRect();
    const after = event.clientY > rect.top + rect.height / 2;
    setActiveDropIndex(rowIndex + (after ? 1 : 0));
  };

  const handleDropAtCurrentIndex = (event) => {
    event.preventDefault();
    const dragged = resolveDraggedCategory(event);
    const target = dropIndexRef.current;
    if (dragged && target !== null) moveCategoryToIndex(dragged, target);
    clearDragState();
  };

  const moveCategoryToPosition = (category, rawPosition) => {
    const order = getCurrentOutputOrder();
    const cleanCategory = String(category || "").toLowerCase().trim();
    const from = order.indexOf(cleanCategory);
    if (from < 0) return false;
    const parsed = Number.parseInt(String(rawPosition ?? "").trim(), 10);
    if (!Number.isFinite(parsed)) return false;
    const targetPosition = Math.min(Math.max(parsed, 1), order.length);
    const targetIndex = targetPosition - 1;
    if (targetIndex === from) return true;
    const [item] = order.splice(from, 1);
    order.splice(targetIndex, 0, item);
    persistCategoryOrder(order, cleanCategory);
    return true;
  };

  const setOrderPositionDraft = (category, value) => {
    const digitsOnly = String(value ?? "").replace(/\D/g, "");
    setOrderPositionDrafts((current) => ({ ...current, [category]: digitsOnly }));
  };

  const clearOrderPositionDraft = (category) => {
    setOrderPositionDrafts((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, category)) return current;
      const next = { ...current };
      delete next[category];
      return next;
    });
  };

  const commitOrderPositionDraft = (category) => {
    const draft = orderPositionDrafts[category];
    if (draft === undefined || String(draft).trim() === "") {
      clearOrderPositionDraft(category);
      return;
    }
    moveCategoryToPosition(category, draft);
    clearOrderPositionDraft(category);
  };

  const moveCategoryBy = (category, delta) => {
    const order = getCurrentOutputOrder();
    const from = order.indexOf(String(category || "").toLowerCase().trim());
    if (from < 0) return;
    moveCategoryToPosition(category, from + 1 + delta);
  };

  const normalizedOrderSearch = orderSearch.trim().toLowerCase();
  const visibleOrderedCategories = orderedCategories
    .map((category, index) => ({ category, index }))
    .filter(({ category }) => !normalizedOrderSearch || category.includes(normalizedOrderSearch));

  if (!canAccessAdminTabs(currentProfile)) {
    return (
      <div className={`mc-enginePage ${embeddedSection ? "mc-enginePage--embeddedConfig" : "mc-configPage mc-tabPage"}`}>
        <div className="mc-enginePage__grid" />
        <div className="mc-enginePage__ambient mc-enginePage__ambient--one" />
        <div className="mc-enginePage__ambient mc-enginePage__ambient--two" />

        {!embeddedSection && <McPageHeader activeKey="config" subtitle="This area is available to admin profiles only." />}
        {!embeddedSection && <McPageFooter />}
      </div>
    );
  }

  return (
    <div className={`mc-enginePage ${embeddedSection ? "mc-enginePage--embeddedConfig" : "mc-configPage mc-tabPage"}`}>
      <div className="mc-enginePage__grid" />
      <div className="mc-enginePage__ambient mc-enginePage__ambient--one" />
      <div className="mc-enginePage__ambient mc-enginePage__ambient--two" />

      {!embeddedSection && <McPageHeader activeKey="config" />}

      <section className="mc-enginePage__heroCards mc-enginePage__heroCards--four">
        <article className="mc-engineCard"><span>Flagging Scope</span><strong>{activeStoreScope === "ALL" ? "All Sites" : formatStoreLabel(activeStoreScope)}</strong></article>
        <article className="mc-engineCard"><span>Default Unit Rule</span><strong>{defaultUnitRule.countUnit || "Unit"} → {defaultUnitRule.systemUnitSize || 1} {defaultUnitRule.baseUnit || "Unit"}</strong></article>
        <article className="mc-engineCard"><span>Mapped Inventory Column</span><strong>{engine.mapping.systemSoh || "Not set"}</strong></article>
        <article className="mc-engineCard"><span>Data Source</span><strong>{String(posSettings.dataSourceMode || "csv").toUpperCase()}</strong></article>
      </section>

      <section className="mc-responseDesignConfig">
        <div className="mc-responseDesignConfig__head">
          <div>
            <div className="mc-enginePage__eyebrow">Response Designed</div>
            <h2>Response Design Configuration</h2>
            <p>Define when Morpheus creates a response, how severity is calculated, and how quickly unresolved matters escalate.</p>
          </div>
          <div className="mc-responseDesignConfig__status">
            <button type="button" className={`mc-configToggle ${responseDesign.enabled ? "is-on" : ""}`} onClick={() => persistResponseDesign({ ...responseDesign, enabled: !responseDesign.enabled })}>
              <span className="mc-configToggle__track"><span className="mc-configToggle__knob" /></span>
              <strong>{responseDesign.enabled ? "Active" : "Off"}</strong>
            </button>
            <small>{responseDesignSaveState === "saving" ? "Saving…" : "All changes saved"}</small>
          </div>
        </div>

        <div className="mc-responseTriggerBox">
          <div><strong>Minimum response trigger</strong><span>Ignore insignificant differences until one of these conditions is met.</span></div>
          <label>Exposure (R)<input type="number" min="0" value={responseDesign.minimumTrigger.exposure} onChange={(e) => persistResponseDesign({ ...responseDesign, minimumTrigger: { ...responseDesign.minimumTrigger, exposure: e.target.value } })} /></label>
          <label>Flagged products<input type="number" min="0" value={responseDesign.minimumTrigger.flaggedProducts} onChange={(e) => persistResponseDesign({ ...responseDesign, minimumTrigger: { ...responseDesign.minimumTrigger, flaggedProducts: e.target.value } })} /></label>
          <label>Rule<select value={responseDesign.minimumTrigger.mode} onChange={(e) => persistResponseDesign({ ...responseDesign, minimumTrigger: { ...responseDesign.minimumTrigger, mode: e.target.value } })}><option value="either">Either condition</option><option value="both">Both conditions</option></select></label>
        </div>

        <div className="mc-responseRulesGrid">
          {[
            ["critical", "Critical", "Immediate executive attention"],
            ["high", "High", "Urgent operational attention"],
            ["medium", "Medium", "Action required within the shift"],
            ["low", "Low", "Monitor and respond when necessary"],
          ].map(([key, label, description]) => (
            <article className={`mc-responseRuleCard is-${key}`} key={key}>
              <div className="mc-responseRuleCard__head"><div><span>{label}</span><small>{description}</small></div></div>
              <div className="mc-responseRuleCard__fields">
                <label>Exposure from (R)<input type="number" min="0" value={responseDesign.severity[key].exposure} onChange={(e) => updateSeverityRule(key, "exposure", e.target.value)} /></label>
                <label>Flagged products from<input type="number" min="0" value={responseDesign.severity[key].flaggedProducts} onChange={(e) => updateSeverityRule(key, "flaggedProducts", e.target.value)} /></label>
                <div className="mc-responseRuleCard__handoff"><strong>Escalation timing</strong><small>Managed per site in People → Escalation.</small></div>
              </div>
            </article>
          ))}
        </div>

        <div className="mc-responseVerificationConfig">
          <div>
            <strong>Correction verification</strong>
            <span>Choose who must approve a submitted correction before Morpheus closes the response.</span>
          </div>
          <label className="mc-responseVerificationConfig__toggle">
            <input type="checkbox" checked={responseDesign.verification?.required !== false} onChange={(e) => persistResponseDesign({ ...responseDesign, verification: { ...responseDesign.verification, required: e.target.checked } })} />
            Verification required
          </label>
          <label>Verified by
            <select value={responseDesign.verification?.role || "operations_manager"} onChange={(e) => persistResponseDesign({ ...responseDesign, verification: { ...responseDesign.verification, role: e.target.value } })}>
              <option value="site_manager">Site Manager</option>
              <option value="operations_manager">Operations Manager</option>
              <option value="owner">Owner</option>
              <option value="same_profile">Same profile that submitted correction</option>
              <option value="custom_profile">Custom profile</option>
            </select>
          </label>
          {responseDesign.verification?.role === "custom_profile" ? <label>Custom profile or role<input value={responseDesign.verification?.customProfile || ""} onChange={(e) => persistResponseDesign({ ...responseDesign, verification: { ...responseDesign.verification, customProfile: e.target.value } })} placeholder="Example: Regional Manager" /></label> : null}
        </div>

        <div className="mc-responseEscalationPreview"><strong>Escalation ownership moved</strong><span>Configure people, channels and response windows per site under People → Escalation.</span><em>Config remains the source of truth for response thresholds and severity only.</em></div>
      </section>

      <section className="mc-maintenancePanel">
        <div>
          <div className="mc-enginePage__eyebrow">Browser cache health</div>
          <h3>MC Cache Maintenance</h3>
          <p>Clears old local session snapshots and archive snapshots that can fill browser storage. It preserves rules, categories, cycles, sections, profiles, active sessions, and cloud data.</p>
          <div className="mc-cacheStats">
            <span>{cacheReport.mcLocalKeys} local MC keys</span>
            <span>{cacheReport.mcSessionKeys} session MC keys</span>
            <span>{cacheReport.removableLocalKeys + cacheReport.removableSessionKeys} safe-to-clear cache keys</span>
          </div>
          {cacheCleanupResult && (
            <div className="mc-cacheResult">
              Cleaned {cacheCleanupResult.removedLocal + cacheCleanupResult.removedSession} cache keys. Refresh once if Categories or Live Count were already open.
            </div>
          )}
        </div>
        <button type="button" className="mc-cacheCleanupButton" onClick={() => setShowCacheConfirm(true)}>
          Clear MC Cache
        </button>
      </section>

      <section className="mc-enginePage__content">
        <article className="mc-enginePanel">
          <div className="mc-enginePanel__header">
            <div>
              <h3>Core config</h3>
              <p>Edit the main engine behavior values.</p>
            </div>
            <div className="mc-engineBadge">Config</div>
          </div>

          <div className="mc-fieldGrid">
            <div className="mc-fieldControl">
              <label>Negative Flag Threshold</label>
              <input
                type="number"
                step="0.01"
                value={flagConfig.negativeFlagThreshold}
                onChange={(e) => persistFlagConfig({ ...flagConfig, negativeFlagThreshold: e.target.value })}
              />
            </div>
            <div className="mc-fieldControl">
              <label>Positive Flag Threshold</label>
              <input
                type="number"
                step="0.01"
                value={flagConfig.positiveFlagThreshold}
                onChange={(e) => persistFlagConfig({ ...flagConfig, positiveFlagThreshold: e.target.value })}
              />
            </div>
            <div className="mc-fieldControl mc-toggleControl">
              <label>Hide Counted in MOS PDF</label>
              <button
                type="button"
                className={`mc-configToggle ${flagConfig.hideCountedInMosPdf ? "is-on" : ""}`}
                onClick={() => persistFlagConfig({ ...flagConfig, hideCountedInMosPdf: !flagConfig.hideCountedInMosPdf })}
                aria-pressed={Boolean(flagConfig.hideCountedInMosPdf)}
              >
                <span className="mc-configToggle__track"><span className="mc-configToggle__knob" /></span>
                <strong>{flagConfig.hideCountedInMosPdf ? "On" : "Off"}</strong>
              </button>
              <small>Only hides counted stock on exported order sheet PDFs. The MOS dialog still shows counted values.</small>
            </div>
            <div className="mc-fieldControl mc-toggleControl">
              <label>Hide Zero Order Items in MOS PDF</label>
              <button
                type="button"
                className={`mc-configToggle ${flagConfig.hideZeroOrderItemsInMosPdf ? "is-on" : ""}`}
                onClick={() => persistFlagConfig({ ...flagConfig, hideZeroOrderItemsInMosPdf: !flagConfig.hideZeroOrderItemsInMosPdf })}
                aria-pressed={Boolean(flagConfig.hideZeroOrderItemsInMosPdf)}
              >
                <span className="mc-configToggle__track"><span className="mc-configToggle__knob" /></span>
                <strong>{flagConfig.hideZeroOrderItemsInMosPdf ? "On" : "Off"}</strong>
              </button>
              <small>Only affects exported order sheet PDFs. Turn on to export ordered items only; turn off to export the full sheet.</small>
            </div>
          </div>

          <div className="mc-helperText">
            Flag rows when cost variance is less than or equal to the negative threshold or greater than or equal to the positive threshold. When All Sites is selected these values apply to every site.
          </div>
        </article>

        <article className="mc-enginePanel mc-posConfigPanel">
          <div className="mc-enginePanel__header">
            <div>
              <h3>POS Integration Foundation</h3>
              <p>Configure the source mode and provider now. API tokens must be added later through Firebase Functions, not the browser.</p>
            </div>
            <div className="mc-engineBadge">POS Ready</div>
          </div>

          <div className="mc-fieldGrid">
            <div className="mc-fieldControl">
              <label>Data Source</label>
              <select value={posSettings.dataSourceMode || "csv"} onChange={(e) => updatePosSettings({ dataSourceMode: e.target.value })}>
                <option value="csv">CSV Upload</option>
                <option value="pos">POS Integration</option>
              </select>
              <small>CSV remains the safe fallback. POS mode uses normalized prepared inventory.</small>
            </div>
            <div className="mc-fieldControl">
              <label>POS Provider</label>
              <select value={posSettings.provider || "lightspeed"} onChange={(e) => updatePosSettings({ provider: e.target.value })}>
                {POS_PROVIDERS.map(p => <option key={p.id} value={p.id}>{p.label}{p.available ? "" : " — Planned"}</option>)}
              </select>
              <small>{getPosProvider(posSettings.provider).setupMessage}</small>
            </div>
            <div className="mc-fieldControl">
              <label>MC Site → POS Outlet</label>
              <input
                value={(posSettings.outletMappings || {})[activeStoreScope || "ALL"] || ""}
                onChange={(e) => updateOutletMapping(e.target.value)}
                placeholder="Example: District Six outlet ID"
              />
              <small>Store the POS outlet/store reference for this MC site.</small>
            </div>
            <div className="mc-fieldControl">
              <label>Connection Status</label>
              <input value={posSettings.status || "not_connected"} readOnly />
              <small>Use Test Connection in Intake after connecting this provider in Super Admin.</small>
            </div>
          </div>

          <div className="mc-posFilterBox" style={{ marginTop: 18 }}>
            <div className="mc-enginePanel__header">
              <div>
                <h3>POS Sync Filters</h3>
                <p>Control which POS items are allowed into MC before Prepared Data is built.</p>
              </div>
              <div className="mc-engineBadge">Sync Rules</div>
            </div>
            <div className="mc-posFilterGrid">
              <div className="mc-posFilterColumn">
                <div className="mc-fieldControl">
                  <label>Count Inclusion Mode</label>
                  <select
                    value={tagFilterMode}
                    onChange={(e) => updateTagFilterMode(e.target.value)}
                  >
                    <option value="all">Count all inventory products</option>
                    <option value="include_tag">Only products with tag</option>
                    <option value="exclude_tag">Exclude products with tag</option>
                  </select>
                  <small>Controls the tag rule for POS syncing.</small>
                </div>
                <div className="mc-fieldControl">
                  <label>Tag name</label>
                  <input
                    value={tagFilterValue}
                    onChange={(e) => updateTagFilterValue(e.target.value)}
                    placeholder="Example: mc or DO_NOT_COUNT"
                    disabled={tagFilterMode === "all"}
                  />
                  <small>{tagFilterMode === "all" ? "No tag filter is applied when counting all inventory products." : "Comma separated tag names can be used when needed."}</small>
                </div>
              </div>

              <div className="mc-posFilterColumn">
                <div className="mc-fieldControl">
                  <label>Category Inclusion Mode</label>
                  <select
                    value={categoryFilterMode}
                    onChange={(e) => updateCategoryFilterMode(e.target.value)}
                    disabled={categoryFiltersDisabled}
                  >
                    <option value="include_categories">Only products with category</option>
                    <option value="exclude_categories">Exclude products with category</option>
                  </select>
                  <small>Category rules can combine with tag rules unless counting all products.</small>
                </div>
                <div className="mc-fieldControl">
                  <label>Category name</label>
                  <input
                    value={categoryFilterValue}
                    onChange={(e) => updateCategoryFilterValue(e.target.value)}
                    placeholder="Example: watch, dress, sunglasses"
                    disabled={categoryFiltersDisabled}
                  />
                  <small>{categoryFiltersDisabled ? "Select a tag rule first to combine category filters." : "Comma separated category names are supported."}</small>
                </div>
              </div>

              <div className="mc-fieldControl mc-toggleControl mc-posFilterZeroToggle">
                <label>Exclude Zero SOH Products</label>
                <button
                  type="button"
                  className={`mc-configToggle ${posSettings.syncFilters?.excludeZeroSoh ? "is-on" : ""}`}
                  onClick={() => updatePosSyncFilter("excludeZeroSoh", !posSettings.syncFilters?.excludeZeroSoh)}
                  aria-pressed={Boolean(posSettings.syncFilters?.excludeZeroSoh)}
                >
                  <span className="mc-configToggle__track"><span className="mc-configToggle__knob" /></span>
                  <strong>{posSettings.syncFilters?.excludeZeroSoh ? "On" : "Off"}</strong>
                </button>
                <small>When on, MC skips items where synced SOH is 0.</small>
              </div>
            </div>
          </div>

          <div className="mc-posFieldMap">
            <strong>Normalized stock row fields</strong>
            <span>product</span>
            <span>sku</span>
            <span>category</span>
            <span>cost</span>
            <span>systemSoh</span>
          </div>
        </article>

        <div className="mc-preparedGrid">
          <article className="mc-enginePanel mc-enginePanel--fill mc-orderWorkspace">
            <div className="mc-enginePanel__header mc-orderWorkspace__header">
              <div>
                <div className="mc-orderWorkspace__eyebrow">DISPLAY SEQUENCE</div>
                <h3>Output Ordering</h3>
                <p>Control the sequence used across Live Count, Inventory Review and reporting. Change a position number or use the movement controls—everything renumbers and saves automatically.</p>
              </div>
              <div className="mc-orderWorkspace__status">
                <strong>{orderedCategories.length}</strong>
                <span>outputs ordered</span>
              </div>
              <MCResponseCard
                visible={orderingResponseVisible}
                type={orderingResponse.type}
                title={orderingResponse.title}
                detail={orderingResponse.detail}
              />
            </div>

            <div className="mc-orderWorkspace__toolbar">
              <label className="mc-orderWorkspace__search">
                <span>Search outputs</span>
                <input
                  type="search"
                  value={orderSearch}
                  onChange={(event) => setOrderSearch(event.target.value)}
                  placeholder="Search by output name..."
                />
              </label>
              <div className="mc-orderWorkspace__guide">
                <strong>Fast reorder</strong>
                <span>Edit the number to move an output directly to any position, then press Enter.</span>
              </div>
              {orderSearch ? (
                <button type="button" className="mc-engineBtn mc-engineBtn--ghost mc-orderWorkspace__clear" onClick={() => setOrderSearch("")}>Clear</button>
              ) : null}
            </div>

            <div className="mc-orderWorkspace__columnHead" aria-hidden="true">
              <span>Position</span>
              <span>Normalized output</span>
              <span>Move</span>
            </div>

            <div className="mc-orderWorkspace__list" onDragOver={(event) => event.preventDefault()}>
              {visibleOrderedCategories.length ? visibleOrderedCategories.map(({ category, index }) => (
                  <div
                    key={category}
                    role="listitem"
                    className={`mc-orderWorkspace__row ${dragCategory === category ? "is-dragging" : ""} ${dropIndex === index ? "is-drop-before" : ""} ${dropIndex === index + 1 ? "is-drop-after" : ""} ${orderingChangedCategory === category && orderingSaveState === "saving" ? "is-saving" : ""} ${orderingChangedCategory === category && orderingSaveState === "saved" ? "is-saved" : ""} ${orderingChangedCategory === category && orderingSaveState === "error" ? "is-error" : ""}`}
                    onDragOver={(event) => handleRowDragOver(event, index)}
                    onDrop={handleDropAtCurrentIndex}
                  >
                    <div className="mc-orderWorkspace__position">
                      <span
                        className="mc-orderWorkspace__grip"
                        title={orderSearch ? "Clear search to drag" : "Drag to reorder"}
                        draggable={!orderSearch}
                        onDragStart={(event) => {
                          if (orderSearch) return;
                          dragCategoryRef.current = category;
                          setDragCategory(category);
                          event.dataTransfer.effectAllowed = "move";
                          event.dataTransfer.setData("text/plain", category);
                        }}
                        onDragEnd={clearDragState}
                      >⋮⋮</span>
                      <input
                        className="mc-orderWorkspace__positionInput"
                        type="text"
                        inputMode="numeric"
                        value={Object.prototype.hasOwnProperty.call(orderPositionDrafts, category) ? orderPositionDrafts[category] : String(index + 1)}
                        aria-label={`Position for ${category}`}
                        disabled={orderingChangedCategory === category && orderingSaveState === "saving"}
                        title="Enter a new order number and press Enter. Leaving it blank restores the previous position."
                        onClick={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                        onDragStart={(event) => event.preventDefault()}
                        onFocus={(event) => event.currentTarget.select()}
                        onChange={(event) => setOrderPositionDraft(category, event.target.value)}
                        onBlur={() => clearOrderPositionDraft(category)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            commitOrderPositionDraft(category);
                            event.currentTarget.blur();
                          } else if (event.key === "Escape") {
                            clearOrderPositionDraft(category);
                            event.currentTarget.blur();
                          }
                        }}
                      />
                    </div>
                    <div className="mc-orderWorkspace__identity">
                      <strong>{category}</strong>
                      <span>Output {index + 1} of {orderedCategories.length}</span>
                    </div>
                    <div className="mc-orderWorkspace__actions">
                      <button type="button" onClick={() => moveCategoryToPosition(category, 1)} disabled={index === 0} title="Move to top">Top</button>
                      <button type="button" onClick={() => moveCategoryBy(category, -1)} disabled={index === 0} title="Move up">↑</button>
                      <button type="button" onClick={() => moveCategoryBy(category, 1)} disabled={index === orderedCategories.length - 1} title="Move down">↓</button>
                      <button type="button" onClick={() => moveCategoryToPosition(category, orderedCategories.length)} disabled={index === orderedCategories.length - 1} title="Move to bottom">Bottom</button>
                    </div>
                  </div>
              )) : (
                <div className="mc-orderWorkspace__empty">No outputs match “{orderSearch}”.</div>
              )}
              {!orderSearch ? (
                <div
                  className={`mc-orderDropZone mc-orderDropZone--end ${dropIndex === orderedCategories.length ? "is-active" : ""}`}
                  onDragOver={(event) => { event.preventDefault(); setActiveDropIndex(orderedCategories.length); }}
                  onDrop={handleDropAtCurrentIndex}
                >
                  Drop here to move to the end
                </div>
              ) : null}
            </div>

            <div className="mc-orderWorkspace__footer">
              <span>Ordering is shared across browsers for the active site.</span>
              <strong>{visibleOrderedCategories.length}{orderSearch ? ` of ${orderedCategories.length}` : ""} shown</strong>
            </div>
          </article>

          <article className="mc-enginePanel mc-enginePanel--fill mc-unitPanel">
            <div className="mc-enginePanel__header">
              <div>
                <h3>Unit Intelligence</h3>
                <p>Universal count units, base units, conversion ratios and price basis for any industry.</p>
              </div>
              <div className="mc-unitHeaderActions">
                <MCResponseCard
                  visible={unitResponseVisible}
                  type={unitResponse.type}
                  title={unitResponse.title}
                  detail={unitResponse.detail}
                  className="mc-unitResponse"
                />
                <div className="mc-engineBadge">UOM Engine</div>
              </div>
            </div>

            <div className="mc-unitHelp">
              <strong>Formula</strong>
              <span>Real quantity = POS SOH × system unit size</span>
              <span>Variance value = variance × price per base unit</span>
            </div>

            <div className={`mc-unitRuleCard mc-unitRuleCard--default ${unitChangedTarget === "default" ? `is-${unitSaveState}` : ""}`}>
              <div className="mc-unitRuleCard__head">
                <div>
                  <h4>Default Unit Rule</h4>
                  <p>Applies to products without a category override.</p>
                </div>
              </div>
              <div className="mc-unitRuleGrid">
                <div className="mc-fieldControl">
                  <label>Count Unit</label>
                  <select value={defaultUnitRule.countUnit || "Unit"} onChange={(e) => updateDefaultUnitRule({ countUnit: e.target.value })}>
                    {unitLibrary.map((unit) => <option key={unit} value={unit}>{unit}</option>)}
                  </select>
                </div>
                <div className="mc-fieldControl">
                  <label>Base Unit</label>
                  <select value={defaultUnitRule.baseUnit || "Unit"} onChange={(e) => updateDefaultUnitRule({ baseUnit: e.target.value })}>
                    {unitLibrary.map((unit) => <option key={unit} value={unit}>{unit}</option>)}
                  </select>
                </div>
                <div className="mc-fieldControl">
                  <label>System Unit Size</label>
                  <input type="text" inputMode="decimal" value={unitDrafts.defaultSystemSize ?? (defaultUnitRule.systemUnitSize ?? 1)} onChange={(e) => setUnitNumberDraft("defaultSystemSize", e.target.value, (value) => updateDefaultUnitRule({ systemUnitSize: value }))} onBlur={() => clearUnitNumberDraft("defaultSystemSize", defaultUnitRule.systemUnitSize, (value) => updateDefaultUnitRule({ systemUnitSize: value }))} />
                  <small>How many base units one POS/count unit contains.</small>
                </div>
                <div className="mc-fieldControl">
                  <label>Price Unit Size</label>
                  <input type="text" inputMode="decimal" value={unitDrafts.defaultPriceSize ?? (defaultUnitRule.priceUnitSize ?? 1)} onChange={(e) => setUnitNumberDraft("defaultPriceSize", e.target.value, (value) => updateDefaultUnitRule({ priceUnitSize: value }))} onBlur={() => clearUnitNumberDraft("defaultPriceSize", defaultUnitRule.priceUnitSize, (value) => updateDefaultUnitRule({ priceUnitSize: value }))} />
                  <small>How many base units the imported price belongs to.</small>
                </div>
                <div className="mc-fieldControl">
                  <label>Price Basis</label>
                  <select value={defaultUnitRule.priceBasis || "count"} onChange={(e) => updateDefaultUnitRule({ priceBasis: e.target.value })}>
                    <option value="count">Price per count unit / pack</option>
                    <option value="base">Price already per base unit</option>
                  </select>
                </div>
                <div className="mc-fieldControl mc-toggleControl">
                  <label>Auto-detect size from product name</label>
                  <button type="button" className={`mc-configToggle ${defaultUnitRule.autoDetectSize ? "is-on" : ""}`} onClick={() => updateDefaultUnitRule({ autoDetectSize: !defaultUnitRule.autoDetectSize })}>
                    <span className="mc-configToggle__track"><span className="mc-configToggle__knob" /></span>
                    <strong>{defaultUnitRule.autoDetectSize ? "On" : "Off"}</strong>
                  </button>
                  <small>Detects 0.5g, 1g, 5L, 24 pack, etc. from product names when possible.</small>
                </div>
              </div>
            </div>

            <div className={`mc-unitLibrary ${unitChangedTarget === "library" ? `is-${unitSaveState}` : ""}`}>
              <div>
                <h4>Unit Library</h4>
                <p>Reusable units for cannabis, restaurants, retail, warehouses and manufacturing.</p>
              </div>
              <div className="mc-unitLibrary__chips">
                {unitLibrary.map((unit) => <span key={unit}>{unit}</span>)}
              </div>
              <div className="mc-unitLibrary__add">
                <input placeholder="Add unit, e.g. Tray" onKeyDown={(e) => { if (e.key === "Enter") { addUnitToLibrary(e.currentTarget.value); e.currentTarget.value = ""; } }} />
                <small>Press Enter to add a custom unit.</small>
              </div>
            </div>

            <div className="mc-unitCategoryStack">
              <div className="mc-unitCategoryStack__head">
                <div>
                  <h4>Category Overrides</h4>
                  <p>Every category uses the Default Unit Rule until you change that category individually.</p>
                </div>
                {Object.keys(categoryUnitRules || {}).length ? (
                  <button type="button" className="mc-engineBtn mc-engineBtn--ghost" onClick={clearAllCategoryUnitRules}>Reset all categories to default</button>
                ) : null}
              </div>
              {liveCategories.map((category) => {
                const rule = { ...defaultUnitRule, ...(categoryUnitRules[category] || {}) };
                const hasOverride = Boolean(categoryUnitRules[category]);
                return (
                  <div className={`mc-unitCategoryRow ${hasOverride ? "is-overridden" : ""} ${unitChangedTarget === `category:${String(category || "").toLowerCase().trim()}` ? `is-${unitSaveState}` : ""}`} key={category}>
                    <div className="mc-unitCategoryRow__title">
                      <strong>{category}</strong>
                      <small>
                        {hasOverride
                          ? `${unitScopePrefix} · Category override`
                          : `${unitScopePrefix} · Default rule`}
                      </small>
                    </div>
                    <div className="mc-unitCategoryRow__fields">
                      <div className="mc-unitMiniField">
                        <label>Count unit</label>
                        <select value={rule.countUnit || "Unit"} onChange={(e) => updateCategoryUnitRule(category, { countUnit: e.target.value })}>
                          {unitLibrary.map((unit) => <option key={unit} value={unit}>{unit}</option>)}
                        </select>
                      </div>
                      <div className="mc-unitMiniField">
                        <label>Base unit</label>
                        <select value={rule.baseUnit || "Unit"} onChange={(e) => updateCategoryUnitRule(category, { baseUnit: e.target.value })}>
                          {unitLibrary.map((unit) => <option key={unit} value={unit}>{unit}</option>)}
                        </select>
                      </div>
                      <div className="mc-unitMiniField mc-unitMiniField--number">
                        <label>System size</label>
                        <input type="text" inputMode="decimal" value={unitDrafts[`category:${category}:system`] ?? (rule.systemUnitSize ?? 1)} onChange={(e) => setUnitNumberDraft(`category:${category}:system`, e.target.value, (value) => updateCategoryUnitRule(category, { systemUnitSize: value }))} onBlur={() => clearUnitNumberDraft(`category:${category}:system`, rule.systemUnitSize, (value) => updateCategoryUnitRule(category, { systemUnitSize: value }))} title="System unit size" />
                      </div>
                      <div className="mc-unitMiniField mc-unitMiniField--number">
                        <label>Price size</label>
                        <input type="text" inputMode="decimal" value={unitDrafts[`category:${category}:price`] ?? (rule.priceUnitSize ?? 1)} onChange={(e) => setUnitNumberDraft(`category:${category}:price`, e.target.value, (value) => updateCategoryUnitRule(category, { priceUnitSize: value }))} onBlur={() => clearUnitNumberDraft(`category:${category}:price`, rule.priceUnitSize, (value) => updateCategoryUnitRule(category, { priceUnitSize: value }))} title="Price unit size" />
                      </div>
                      <div className="mc-unitMiniField mc-unitMiniField--basis">
                        <label>Price basis</label>
                        <select value={rule.priceBasis || "count"} onChange={(e) => updateCategoryUnitRule(category, { priceBasis: e.target.value })}>
                          <option value="count">Price per count unit</option>
                          <option value="base">Price per base unit</option>
                        </select>
                      </div>
                      <div className="mc-unitCategoryRow__actions">
                        <button type="button" className={`mc-unitAuto ${rule.autoDetectSize ? "is-on" : ""}`} onClick={() => updateCategoryUnitRule(category, { autoDetectSize: !rule.autoDetectSize })}>Auto</button>
                        <button type="button" className="mc-unitClear" onClick={() => clearCategoryUnitRule(category)}>Reset</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </article>
        </div>
      </section>
      {showCacheConfirm && (
        <div className="mc-modalBackdrop" role="presentation">
          <div className="mc-modalCard mc-cacheConfirmModal" role="dialog" aria-modal="true" aria-labelledby="mc-cache-confirm-title">
            <div className="mc-enginePage__eyebrow">Browser cache health</div>
            <h3 id="mc-cache-confirm-title">Clear old Morpheus cache?</h3>
            <p>MC will remove heavy local and session snapshots only. Your category rules, category order, unit intelligence, cycles, sections, profiles, active sessions, and cloud data are preserved.</p>
            <div className="mc-cacheConfirmList">
              <span>Clears old session snapshots</span>
              <span>Clears old archive snapshots</span>
              <span>Preserves settings and live data</span>
            </div>
            <div className="mc-modalActions">
              <button type="button" className="mc-modalButton mc-modalButton--ghost" onClick={() => setShowCacheConfirm(false)}>Cancel</button>
              <button type="button" className="mc-modalButton mc-modalButton--primary" onClick={runCacheCleanup}>Clear MC Cache</button>
            </div>
          </div>
        </div>
      )}

      {!embeddedSection && <McPageFooter />}
    </div>
  );
}
